import {
  Button,
  Divider,
  FormControlLabel,
  Grid,
  RadioGroup,
  Typography,
} from '@forgeglobal/design-framework';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { MaritalStatus } from '@tcs/client-portal-api/types/Beneficiaries';
import {
  Beneficiary,
  BeneficiaryType,
  RelationshipType,
} from '@tcs/client-portal-api/types/Beneficiary';
import useProfile from '@tcs/client-portal-common/hooks/useProfile';
import { STATES } from '@tcs/common/assets/constants';
import DropdownField from '@tcs/common/components/atoms/DropdownField';
import Form, {
  Field,
  FormRenderProps,
  useFormState,
} from '@tcs/common/components/atoms/Form';
import PhoneNumberTextField from '@tcs/common/components/atoms/PhoneNumberTextField';
import Radio from '@tcs/common/components/atoms/Radio';
import TextField from '@tcs/common/components/atoms/TextField';
import ZipCodeTextField from '@tcs/common/components/atoms/ZipCodeTextField';
import { Unicode } from '@tcs/common/utils/unicode';
import { FormApi } from 'final-form';
import parsePhoneNumber from 'libphonenumber-js';
import { pick } from 'lodash';
import { useState } from 'react';
import useStyles from '../style';
import {
  beneficiaryFormSchema,
  useBeneficiariesErrors,
} from './BeneficiariesValidators';
import EntityBeneficiaryForm from './EntityBeneficiaryForm';
import IndividualBeneficiaryForm from './IndividualBeneficiaryForm';

interface BeneficiaryFormProps {
  accountNumber: string;
  name: string;
  beneficiary: Beneficiary | Partial<Beneficiary>;
  formTitle: string;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  isPrimary: boolean;
}

const DEFAULT_FORM_VALUES: Partial<Beneficiary> = {
  beneficiaryType: BeneficiaryType.Individual,
};

export const BeneficiaryForm = ({
  accountNumber,
  name,
  beneficiary = DEFAULT_FORM_VALUES,
  formTitle,
  onCancel,
  onSubmit,
  isPrimary,
}: BeneficiaryFormProps) => {
  const classes = useStyles();
  const { data: profile } = useProfile(accountNumber);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const parentFormState = useFormState();

  const { isCommunityPropertyState } = useBeneficiariesErrors(
    accountNumber,
    name
  );

  const getNonMarriedRelationshipForCommunityPropertyState = (relationship) => {
    // If relation is non-spouse, return non-spouse
    // For a new beneficiary, since user is not married in a community property state, pre-fill non-spouse.
    // If this is an existing beneficiary, return undefined if relationship is not a valid value so user can select a valid relationship.
    if (relationship === RelationshipType.NonSpouse) {
      return RelationshipType.NonSpouse;
    }

    if (formTitle.startsWith('Add')) {
      return RelationshipType.NonSpouse;
    }
    return undefined;
  };

  const getMarriedRelationshipForCommunityPropertyState = (relationship) => {
    if (relationship === RelationshipType.NotarizedSpouse) {
      return RelationshipType.NotarizedSpouse;
    }
    if (relationship === RelationshipType.NotarizedNonSpouse) {
      return RelationshipType.NotarizedNonSpouse;
    }
    return RelationshipType.Spouse;
  };

  const communityPropertyStateValues: Partial<Beneficiary> =
    isCommunityPropertyState
      ? {
          beneficiaryType: BeneficiaryType.Individual,
          relationship:
            parentFormState.values.maritalStatus.maritalStatus ===
            MaritalStatus.Married
              ? getMarriedRelationshipForCommunityPropertyState(
                  parentFormState.values.relationship
                )
              : getNonMarriedRelationshipForCommunityPropertyState(
                  parentFormState.values.relationship
                ),
          percentage:
            parentFormState.values.maritalStatus === MaritalStatus.Married
              ? 100
              : undefined,
        }
      : {};

  const onClickApplyContactDetails = (form: FormApi<Record<string, any>>) => {
    form.batch(() => {
      form.change('address', profile.address.addressLine1);
      form.change('city', profile.address.city);
      form.change('state', profile.address.state);
      form.change('zipCode', profile.address.zipCode);
      form.change('email', profile.contactInfo.email);
      const phoneNumber = parsePhoneNumber(
        profile.contactInfo.primaryPhone,
        'US'
      );
      form.change('phone', phoneNumber.formatInternational());
    });
  };

  const onClickConfirm = (errors) => {
    if (Object.keys(errors)?.length > 0) {
      setShowErrorDialog(true);
    }
  };

  const handleOnSubmit = (data) => {
    const addressKeys = [
      'address',
      'city',
      'state',
      'zipCode',
      'phone',
      'email',
    ];

    let keys = [
      'id',
      'beneficiaryType',
      'percentage',
      'relationship',
      'firstName',
      'middleName',
      'lastName',
      'dateOfBirth',
      'socialSecurityNumber',
      ...addressKeys,
    ];

    if (data.beneficiaryType === BeneficiaryType.Entity) {
      keys = [
        'id',
        'beneficiaryType',
        'percentage',
        'entityType',
        'entityName',
        'taxIdentificationNumber',
        ...addressKeys,
      ];
    }

    onSubmit(pick(data, keys));
  };

  return (
    <Form
      onSubmit={handleOnSubmit}
      initialValues={{
        ...communityPropertyStateValues,
        ...beneficiary,
      }}
      validationSchema={beneficiaryFormSchema()}
      render={({
        handleSubmit,
        submitting,
        values,
        errors,
        form,
      }: FormRenderProps) => (
        <form
          onSubmit={handleSubmit}
          data-test-id="beneficiary-form"
          noValidate>
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <Typography
                data-test-id="title"
                variant="h5"
                className={classes.title}>
                {formTitle}
              </Typography>
              <Typography variant="body2">
                Required fields are indicated with asterisk (*)
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2">Beneficiary type</Typography>
              <RadioGroup
                className={classes.radiogroup}
                data-test-id="beneficiary-form-type">
                <FormControlLabel
                  label="Individual"
                  control={
                    <Field
                      name="beneficiaryType"
                      value="Individual"
                      type="radio"
                      render={(props: any) => {
                        return <Radio {...props} />;
                      }}
                    />
                  }
                />
                <FormControlLabel
                  label="Entity"
                  control={
                    <Field
                      name="beneficiaryType"
                      value="Entity"
                      type="radio"
                      render={(props: any) => {
                        return <Radio {...props} />;
                      }}
                    />
                  }
                />
              </RadioGroup>
            </Grid>
            {values.beneficiaryType === 'Individual' && (
              <IndividualBeneficiaryForm form={form} isPrimary={isPrimary} />
            )}
            {values.beneficiaryType === 'Entity' && (
              <EntityBeneficiaryForm isPrimary={isPrimary} />
            )}
            <Grid item>
              <Divider />
            </Grid>
            <Grid item>
              <Button
                onClick={() => onClickApplyContactDetails(form)}
                fullWidth>
                Copy account contact information
              </Button>
            </Grid>
            <Grid item>
              <Field
                name="address"
                render={(props) => (
                  <TextField
                    label="Address"
                    data-private
                    {...props}
                    normalize
                  />
                )}
              />
            </Grid>
            <Grid container item spacing={2}>
              <Grid item xs={12} sm={4}>
                <Field
                  name="city"
                  render={(props) => (
                    <TextField label="City" data-private {...props} normalize />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field
                  name="state"
                  render={(props) => (
                    <DropdownField
                      label="State"
                      {...props}
                      options={[
                        { label: Unicode.EmDash, value: '' },
                        ...STATES.map(({ Id, Name }) => ({
                          label: Name,
                          value: Id,
                        })),
                      ]}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field
                  name="zipCode"
                  render={(props) => (
                    <ZipCodeTextField
                      label="Zip code"
                      data-private
                      {...props}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item>
              <Field
                name="phone"
                render={(props) => (
                  <PhoneNumberTextField
                    data-private
                    label="Phone number"
                    {...props}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Field
                name="email"
                render={(props) => (
                  <TextField
                    data-private
                    label="Email address"
                    {...props}
                    normalize
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            spacing={2}
            justifyContent="flex-end"
            pt={4}
            mb={4}>
            <Grid item>
              <Button
                size="medium"
                color="secondary"
                onClick={onCancel}
                data-test-id="cancel-button">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="medium"
                color="primary"
                type="submit"
                disabled={submitting}
                data-test-id="confirm-button"
                onClick={() => onClickConfirm(errors)}>
                Confirm
              </Button>
            </Grid>
          </Grid>
          <Dialog open={showErrorDialog} maxWidth="xs" fullWidth>
            <DialogContent className={classes.dialogContent}>
              <DialogContentText>
                Please enter all required fields
              </DialogContentText>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button onClick={() => setShowErrorDialog(false)}>Ok</Button>
            </DialogActions>
          </Dialog>
        </form>
      )}
    />
  );
};

export default BeneficiaryForm;
